import { motion } from "framer-motion";
import { GraduationCap, MapPin, Calendar, Award } from "lucide-react";
import data from "../data/software.json";

const Education = () => {
  const education = data["Education"];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-secondary-900 mb-4">
            Education
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            My academic journey and the foundation that shaped my career in
            software development.
          </p>
        </motion.div>

        {/* Education Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-primary-500"
        >
          <div className="flex flex-col lg:flex-row items-start space-y-6 lg:space-y-0 lg:space-x-8">
            {/* University Logo */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-secondary-100 rounded-xl flex items-center justify-center">
                <img
                  src={education.degree.icon}
                  alt="University of Illinois logo"
                  className="w-20 h-20 object-contain"
                />
              </div>
            </div>

            {/* Education Details */}
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <GraduationCap className="w-5 h-5 text-primary-600" />
                <h2 className="font-display font-bold text-2xl text-secondary-900">
                  {education.degree.name}
                </h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-secondary-600">
                  <Award className="w-4 h-4" />
                  <span className="font-medium">{education.degree.degree}</span>
                </div>

                <div className="flex items-center space-x-2 text-secondary-600">
                  <MapPin className="w-4 h-4" />
                  <span>Mathematics and Computer Science</span>
                </div>

                <div className="flex items-center space-x-2 text-secondary-600">
                  <Calendar className="w-4 h-4" />
                  <span>Graduated {education.degree.graduatingYear}</span>
                </div>
              </div>

              {/* Key Highlights */}
              <div className="mt-8">
                <h3 className="font-display font-semibold text-lg text-secondary-900 mb-4">
                  Key Highlights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <h4 className="font-medium text-secondary-900 mb-2">
                      Mathematics Focus
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Strong foundation in mathematical concepts and
                      problem-solving skills
                    </p>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <h4 className="font-medium text-secondary-900 mb-2">
                      Computer Science
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Comprehensive understanding of algorithms, data
                      structures, and software engineering
                    </p>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <h4 className="font-medium text-secondary-900 mb-2">
                      Liberal Arts
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Well-rounded education with emphasis on critical thinking
                      and communication
                    </p>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <h4 className="font-medium text-secondary-900 mb-2">
                      Research Experience
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Hands-on experience with real-world software development
                      projects
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Additional Information */}
        <motion.div
          variants={itemVariants}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {/* University Information */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6">
            <h3 className="font-display font-semibold text-xl text-secondary-900 mb-4">
              About UIUC
            </h3>
            <p className="text-secondary-700 leading-relaxed">
              The University of Illinois at Urbana-Champaign is a world-renowned
              institution known for its excellence in computer science and
              engineering. The university consistently ranks among the top
              programs globally and has produced numerous successful software
              engineers and technology leaders.
            </p>
          </div>

          {/* Career Impact */}
          <div className="bg-gradient-to-br from-accent-50 to-accent-100 rounded-xl p-6">
            <h3 className="font-display font-semibold text-xl text-secondary-900 mb-4">
              Career Impact
            </h3>
            <p className="text-secondary-700 leading-relaxed">
              My education at UIUC provided the theoretical foundation and
              practical skills that have been instrumental in my career. The
              combination of mathematics and computer science has given me a
              unique perspective on solving complex technical challenges in the
              industry.
            </p>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div variants={itemVariants} className="mt-12 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
              Continuous Learning
            </h3>
            <p className="text-secondary-600 max-w-2xl mx-auto mb-6">
              Education doesn't end with a degree. I'm committed to continuous
              learning and staying current with the latest technologies and
              industry best practices.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-secondary-100 px-3 py-1 rounded-full">
                Online Courses
              </span>
              <span className="bg-secondary-100 px-3 py-1 rounded-full">
                Certifications
              </span>
              <span className="bg-secondary-100 px-3 py-1 rounded-full">
                Conferences
              </span>
              <span className="bg-secondary-100 px-3 py-1 rounded-full">
                Open Source
              </span>
              <span className="bg-secondary-100 px-3 py-1 rounded-full">
                Mentoring
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Education;
